import React, { forwardRef } from 'react';
import { styled as styled2 } from '@compiled/react';
import { xcss, Pressable, Box, Inline } from '@atlaskit/primitives';
import { Lozenge } from './lozenge/index.tsx';
import type { Props } from './types.tsx';

export const TEST_ID_PREFIX = 'issue.fields.status.common.ui.status-lozenge.';

const CustomChevronDownCustomIcon = (props: { label: string }) => (
	<svg
		width={12}
		height={12}
		viewBox="5 5 13 13"
		aria-label={props.label || undefined}
		role={props.label ? 'img' : undefined}
	>
		<path
			d="M8.292 10.293a1.009 1.009 0 0 0 0 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 0 0 0-1.419.987.987 0 0 0-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 0 0-1.406 0z"
			fill="currentColor"
			fillRule="evenodd"
		/>
	</svg>
);

export const StatusLozenge = forwardRef<HTMLElement, Props>(
	(
		{
			status: { name, statusCategory },
			isEditable,
			hasHoverState,
			shouldShowDropdownIndication,
			isDisabled = false,
		}: Props,
		ref,
	) => (
		<>
			<StatusContentWrapper
				data-testid={`${TEST_ID_PREFIX}${statusCategory.id}`}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				ref={ref as React.RefObject<HTMLDivElement>}
			>
				{isEditable === true || shouldShowDropdownIndication === true ? (
					<Lozenge statusCategoryId={statusCategory.id} isDisabled={isDisabled}>
						<StyledLozengeContentWrapper>
							<Inline alignBlock="center">
								{name}
								<CustomChevronDownCustomIcon label="" />
							</Inline>
						</StyledLozengeContentWrapper>
					</Lozenge>
				) : (
					<Lozenge statusCategoryId={statusCategory.id} isDisabled={isDisabled}>
						{name}
					</Lozenge>
				)}
			</StatusContentWrapper>
			{hasHoverState && <Box xcss={hoverOverlayStyles} />}
		</>
	),
);

export const WrappedStatusLozenge = (props: Props) => {
	const {
		ariaLabel,
		isEditable,
		isOpen,
		targetRef,
		contentRef,
		triggerProps = {},
		...others
	} = props;

	return (
		<Pressable
			xcss={pressableStyles}
			ref={targetRef}
			aria-label={ariaLabel}
			aria-expanded={isOpen}
			isDisabled={!isEditable}
			// When the popup is open we don't want to track the click on this button as it solely closes
			// the popup.
			// The logic here may look incorrect, but it turns out that when clicking this button the
			// `isOpen` toggle happens before the `onClick` handler that sends the press tracing.
			// We need to disable the `jira/ufo/valid-labels` lint rule
			// because for this particular interaction we only want to track
			// opening the status popup.

			interactionName={isOpen ? 'issue.fields.status.popup.open' : undefined}
			{...triggerProps}
		>
			<StatusLozenge {...others} isEditable={isEditable} ref={contentRef} />
		</Pressable>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLozengeContentWrapper = styled2.div({
	alignItems: 'center',
	display: 'contents',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusContentWrapper = styled2.div({
	display: 'flex',
	width: '100%',
});

const pressableStyles = xcss({
	border: 'none',
	padding: 'space.0',
	borderRadius: 'radius.small',
	background: 'transparent',
	position: 'relative',
	maxWidth: '100%',
	marginLeft: 'space.025',
});

const hoverOverlayStyles = xcss({
	position: 'absolute',
	inset: '0',
	borderRadius: 'radius.small',
	marginRight: 'space.025',
	':hover': {
		opacity: 0.3,
		backgroundColor: 'color.background.inverse.subtle',
	},
});
